import dayjs, { Dayjs } from 'dayjs';

//https://day.js.org/docs/en/installation/installation
//dayjs with the plugins: utc, ...
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(timezone);

/** Any value that dayjs can parse: an ISO-ish string, a `Date`, a unix timestamp, or `null`/`undefined` for "now". */
type DateInput = string | Date | number | null | undefined;

/**
 * Converts a UTC time to local time.
 * @param utc - UTC time as a string, Date object, timestamp, or null for current time
 * @returns Local time as a Dayjs object
 */
const toLocalTime = function (utc: DateInput = null): Dayjs {
    let localdayjs = (utc ? dayjs.utc(utc) : dayjs.utc()).local();
    return localdayjs;
};

/**
 * Converts a UTC time to local time and formats it using the default format.
 * @param utc - UTC time as a string, Date object, timestamp, or null for current time
 * @returns Formatted local time, or null if the resolved date is falsy
 */
const formatLocalTime = function (utc: DateInput = null): string | null {
    return formatDateTime(toLocalTime(utc));
};

/**
 * Wraps a date-like value in a Dayjs instance.
 * @param date - Any value accepted by `dayjs()`
 * @returns The corresponding Dayjs object
 */
const toDateTime = function (date: DateInput | Dayjs): Dayjs {
    return dayjs(date);
};

/**
 * Formats a date-like value as a date and time string.
 * @param date - Any value accepted by `dayjs()`
 * @param format - dayjs format string. Default: `'MM-DD-YYYY hh:mm A'`
 * @returns The formatted string, or null when `date` is falsy
 */
const formatDateTime = function (date: DateInput | Dayjs, format = 'MM-DD-YYYY hh:mm A'): string | null {
    if (!date) return null;
    return toDateTime(date).format(format);
};

/**
 * Formats a date-like value as a date-only string.
 * @param date - Any value accepted by `dayjs()`
 * @param format - dayjs format string. Default: `'MM-DD-YYYY'`
 * @returns The formatted string, or null when `date` is falsy
 */
const formatDate = function (date: DateInput | Dayjs, format = 'MM-DD-YYYY'): string | null {
    if (!date) return null;
    return toDateTime(date).format(format);
};

/**
 * Parses a string against a list of candidate input formats (in UTC) and re-formats it with `outputFormat`.
 *
 * Note: the `customParseFormat` dayjs plugin is not loaded in this module, so `inputFormats`
 * does not strictly constrain parsing — `dayjs.utc(input, format)` falls back to dayjs's
 * default (lenient) parsing when the format doesn't apply.
 * @param input - The value to parse; non-string values are used as-is
 * @param inputFormats - Candidate dayjs parse formats to try, in order, until one is valid
 * @param outputFormat - dayjs format string used for the result
 * @returns The formatted string, or `''` if no format matched
 */
const formatTime = (input: DateInput | Dayjs, inputFormats: string[], outputFormat: string): string => {
    let dateObj: Dayjs = input as Dayjs;

    if (typeof input === 'string') {
        for (let i = 0; i < inputFormats.length; i++) {
            const format = inputFormats[i];
            dateObj = dayjs.utc(input, format);
            if (dateObj.isValid()) {
                break;
            }
        }
    }

    return dateObj.isValid() ? dateObj.format(outputFormat) : '';
};

/**
 * Checks whether a value matches one of a fixed set of time-only formats
 * (`'hh:mm A'`, `'HH:mm:ss'`, `'HH:mm'`).
 *
 * Note: the `customParseFormat` dayjs plugin is not loaded in this module, so the `format`
 * argument to `dayjs()` is not enforced — this does not "strictly" validate the format, only
 * that dayjs's default parsing produces a valid date.
 * @param value - The value to validate
 * @returns True if the value matches one of the supported time formats
 */
function isValidTime (value: string | Date | number): boolean {
    const formats = ['hh:mm A', 'HH:mm:ss', 'HH:mm'];
    for (let i = 0; i < formats.length; i++) {
        const format = formats[i];
        if (dayjs(value, format, true).isValid()) {
            return true;
        }
    }
    return false;
}

export { dayjs, toDateTime, formatDateTime, formatDate, toLocalTime, formatLocalTime, formatTime, isValidTime };
